import type { OemSerialNumber } from "@xlabmedical/medic-web-framework";

export enum AppEnvironmentType {
    BETA = "beta",
    GAMA = "gama",
    SIGMA = "sigma",
    PRODUCTION = "production",
    LOCALHOST = "localhost",
}

export function getHostname() {
    return window.location.hostname;
}

export function getOrigin() {
    return window.location.origin;
}

export interface AppEnvironment {
    DEV?: boolean;
    PROD?: boolean;
    SSR?: boolean;
    VITE_APP_ENVIRONMENT?: AppEnvironmentType;
    VITE_BUILD_VERSION?: string;
    VITE_BUILD_DATE?: string;
    VITE_WEBSOCKET_API_URL?: string;
    BASE_URL: string;
    NODE_ENV: string;
    MODE?: string;
}

export function getEnv(): AppEnvironment {
    return import.meta.env as AppEnvironment;
}
export function isDevelopment() {
    return getEnv().DEV;
}

function getWebUrl() {
    const dev = getEnv().DEV;
    if (dev) {
        return getOrigin();
    } else {
        return "/planner";
    }
}

function getApiUrl(apiUrl: string) {
    const dev = getEnv().DEV;

    if (dev) {
        return apiUrl;
    } else {
        return `${getOrigin()}/api/`;
    }
}

function getWSUrl(wsUrl: string) {
    const env = getEnv();
    const override = env.VITE_WEBSOCKET_API_URL?.trim();
    if (override) {
        return override;
    }
    const dev = env.DEV;
    if (dev) {
        return wsUrl;
    } else {
        return `wss://${getHostname()}/notifierws`;
    }
}

const ENVIRONMENT_CONFIG = {
    [AppEnvironmentType.PRODUCTION]: {
        WEB_URL: getWebUrl(),
        API_URL: getApiUrl(""),
        WS_URL: getWSUrl(""),
    },
    [AppEnvironmentType.BETA]: {
        WEB_URL: getWebUrl(),
        API_URL: getApiUrl("https://beta-cloud.medicteam.io/api/"),
        WS_URL: getWSUrl("wss://beta-cloud.medicteam.io:443/notifierws"),
    },
    [AppEnvironmentType.GAMA]: {
        WEB_URL: getWebUrl(),
        API_URL: getApiUrl("https://gama-cloud.medicteam.io/api/"),
        WS_URL: getWSUrl("wss://gama-cloud.medicteam.io:443/notifierws"),
    },
    [AppEnvironmentType.SIGMA]: {
        WEB_URL: getWebUrl(),
        API_URL: getApiUrl("https://sigma-cloud.medicteam.io/api/"),
        WS_URL: getWSUrl("wss://sigma-cloud.medicteam.io:443/notifierws"),
    },
    [AppEnvironmentType.LOCALHOST]: {
        WEB_URL: getWebUrl(),
        API_URL: "http://localhost:8080/api/",
        WS_URL: getWSUrl("ws://localhost:8080/notifierws"),
    },
};

export class GlobalConstantsInterface {
    public ENVIRONMENT: AppEnvironmentType = (getEnv().VITE_APP_ENVIRONMENT as AppEnvironmentType) ?? AppEnvironmentType.BETA;
    public BASE_URL = ENVIRONMENT_CONFIG[this.ENVIRONMENT].WEB_URL;
    public BASE_API_URL = ENVIRONMENT_CONFIG[this.ENVIRONMENT].API_URL;
    public WEBSOCKET_API_URL = ENVIRONMENT_CONFIG[this.ENVIRONMENT].WS_URL;
    public BUILD_NUMBER = getEnv().VITE_BUILD_VERSION;
    public VENDOR_NAME: string | undefined = undefined;
    public VENDOR_CODE_TAG: string | undefined = undefined;
    public VENDOR_ID: OemSerialNumber | undefined = undefined;
}

const GlobalConstants = new GlobalConstantsInterface();
export { GlobalConstants };
